"use client";

import { getGroundingPlatformLogo } from "@repo/core/client";
import {
	AlertTriangle,
	ChevronDown,
	ChevronUp,
	ExternalLink,
	Search,
	Zap,
} from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";

interface GroundingSearch {
	id: string;
	query: string;
	orderInResponse?: number;
	searchContext?: string;
	rankings: Array<{
		id: string;
		domain: string;
		isOurBrand: boolean;
		isCompetitor: boolean;
		rankPosition: number | null;
		url: string | null;
		title: string | null;
		description: string | null;
		searchEngine?: string;
	}>;
}

interface GroundingSearchesSectionProps {
	groundingSearches: GroundingSearch[];
	platformName: string;
	/** Platform id for logo (e.g. openai, gemini); Gemini shows as Google for grounding context */
	platform?: string;
	brandDomain?: string;
	/** If true, show in a more prominent highlighted style */
	highlighted?: boolean;
	/**
	 * False for reports generated while grounding-search rank tracking is paused
	 * (`GroundingSearchListV2`): no Google/Brave checks ran, so there are no
	 * ranks, gaps or "not ranked" states to show, only the searches. Defaults to
	 * true so every other caller renders exactly as before.
	 */
	rankTracking?: boolean;
}

export function GroundingSearchesSection({
	groundingSearches,
	platformName,
	platform,
	brandDomain,
	highlighted = false,
	rankTracking = true,
}: GroundingSearchesSectionProps) {
	const t = useTranslations();

	// Claude grounds in Brave; ChatGPT/Gemini ground in Google. Pick the engine
	// for this platform tab so we show the right rankings + logo.
	const isBrave = platform === "claude";
	const engineLogoSrc = isBrave
		? "/images/brave-logo.svg"
		: "/images/google-logo.svg";
	const engineAlt = isBrave ? "Brave" : "Google";

	// Only consider rankings for the engine this platform grounds in.
	const engineRankingsFor = (gs: GroundingSearch) =>
		(rankTracking ? gs.rankings || [] : []).filter((r) =>
			isBrave
				? r.searchEngine === "brave"
				: !r.searchEngine || r.searchEngine === "google",
		);

	// Count how many searches where brand ranks in top 30
	const brandRanks = groundingSearches.filter((gs) =>
		engineRankingsFor(gs).some(
			(r) => r.isOurBrand && r.rankPosition !== null,
		),
	).length;

	// Calculate gap opportunities (competitor ranks, brand doesn't)
	const gapOpportunities = groundingSearches.filter((gs) => {
		const rankings = engineRankingsFor(gs);
		if (rankings.length === 0) return false;

		const brandRanked = rankings.some(
			(r) => r.isOurBrand && r.rankPosition !== null,
		);
		const competitorRanked = rankings.some(
			(r) => r.isCompetitor && r.rankPosition !== null,
		);

		return !brandRanked && competitorRanked;
	}).length;

	// Default to expanded if there are gap opportunities
	const [expanded, setExpanded] = useState(gapOpportunities > 0);

	if (!groundingSearches || groundingSearches.length === 0) {
		return null;
	}

	const hasHighlightableInsights = gapOpportunities > 0 || brandRanks > 0;

	return (
		<div
			className={`mt-4 rounded-lg border ${
				hasHighlightableInsights
					? "border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50"
					: "border-gray-200 bg-gray-50"
			} overflow-hidden`}
		>
			{/* Header */}
			<button
				type="button"
				onClick={() => setExpanded(!expanded)}
				className={`flex items-center justify-between w-full text-left p-4 ${
					hasHighlightableInsights
						? "hover:bg-amber-100/50"
						: "hover:bg-gray-100"
				} transition-colors`}
			>
				<div className="flex items-center space-x-3">
					<div
						className={`p-2 rounded-lg ${hasHighlightableInsights ? "bg-amber-200" : "bg-gray-200"}`}
					>
						<Search
							className={`h-4 w-4 ${hasHighlightableInsights ? "text-amber-700" : "text-gray-600"}`}
						/>
					</div>
					<div>
						<div className="flex items-center gap-2">
							{platform && getGroundingPlatformLogo(platform) && (
								<Image
									src={getGroundingPlatformLogo(platform)!}
									alt={platformName}
									width={20}
									height={20}
									className="rounded-sm"
								/>
							)}
							<span className="text-sm font-semibold text-gray-900">
								AI Grounding Searches
							</span>
							{hasHighlightableInsights && (
								<Zap className="h-4 w-4 text-amber-500" />
							)}
						</div>
						<p className="text-xs text-gray-500">
							Searches {platformName} performed to answer this
							query
						</p>
					</div>
				</div>

				<div className="flex items-center gap-2">
					{/* Quick Stats */}
					<div className="flex items-center gap-1.5">
						<span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
							{groundingSearches.length} searches
						</span>
						{brandRanks > 0 && (
							<span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
								✓ {brandRanks} ranked
							</span>
						)}
						{gapOpportunities > 0 && (
							<span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
								<AlertTriangle className="h-3 w-3" />
								{gapOpportunities} gap
								{gapOpportunities !== 1 ? "s" : ""}
							</span>
						)}
					</div>
					{expanded ? (
						<ChevronUp className="h-5 w-5 text-gray-400" />
					) : (
						<ChevronDown className="h-5 w-5 text-gray-400" />
					)}
				</div>
			</button>

			{expanded && (
				<div className="p-4 pt-0 space-y-3">
					{rankTracking ? (
						<p className="text-xs text-gray-600 bg-white/50 rounded p-2 border border-gray-200">
							💡 <strong>Why this matters:</strong> These searches
							influence which brands {platformName} recommends.
							Ranking for these queries improves your AI
							visibility.
						</p>
					) : (
						<p className="text-xs text-gray-600 bg-white/50 rounded p-2 border border-gray-200">
							<strong>Why this matters:</strong> These searches
							shape which sources {platformName} reads before it
							answers. Writing content that targets them is an
							important step toward getting cited.
						</p>
					)}

					{groundingSearches.map((search, index) => {
						const allRankings = rankTracking
							? search.rankings || []
							: [];
						// Claude grounds in Brave; ChatGPT/Gemini ground in Google.
						const engineRankings = isBrave
							? allRankings.filter(
									(r) => r.searchEngine === "brave",
								)
							: allRankings.filter(
									(r) =>
										!r.searchEngine ||
										r.searchEngine === "google",
								);

						const brandRankingEngine = engineRankings.find(
							(r) => r.isOurBrand,
						);

						const competitorRankingsEngine = engineRankings
							.filter(
								(r) =>
									r.isCompetitor && r.rankPosition !== null,
							)
							.sort(
								(a, b) =>
									(a.rankPosition || 999) -
									(b.rankPosition || 999),
							);

						const bestCompetitorRankEngine =
							competitorRankingsEngine.length > 0
								? competitorRankingsEngine[0].rankPosition
								: null;

						const hasAnyRankings = engineRankings.length > 0;

						const isGap =
							hasAnyRankings &&
							!brandRankingEngine?.rankPosition &&
							bestCompetitorRankEngine !== null;

						return (
							<div
								key={search.id}
								className={`p-3 rounded-lg border ${
									isGap
										? "border-orange-200 bg-orange-50"
										: "border-gray-200 bg-gray-50"
								}`}
							>
								<div className="flex items-start justify-between">
									<div className="flex-1">
										<div className="flex items-center space-x-2 mb-1">
											<span className="text-xs font-medium text-gray-500">
												#{index + 1}
											</span>
											<span className="text-sm font-medium text-gray-900">
												"{search.query}"
											</span>
										</div>

										{rankTracking &&
											search.rankings &&
											search.rankings.length > 0 && (
												<div className="mt-2 space-y-2">
													{/* Brand ranking */}
													{brandRankingEngine && (
														<div className="space-y-1">
															<div className="text-xs font-medium text-gray-700">
																{brandDomain ||
																	"Your brand"}
															</div>
															<div className="flex flex-wrap gap-2">
																<div className="flex items-center space-x-1 text-xs">
																	<Image
																		src={
																			engineLogoSrc
																		}
																		alt={
																			engineAlt
																		}
																		width={
																			14
																		}
																		height={
																			14
																		}
																		className="flex-shrink-0"
																	/>
																	{brandRankingEngine.rankPosition !==
																	null ? (
																		<>
																			<span className="inline-flex items-center px-1.5 py-0.5 rounded font-medium bg-green-100 text-green-800">
																				#
																				{
																					brandRankingEngine.rankPosition
																				}
																			</span>
																			{brandRankingEngine.url && (
																				<a
																					href={
																						brandRankingEngine.url
																					}
																					target="_blank"
																					rel="noopener noreferrer"
																					className="text-blue-600 hover:text-blue-700 flex items-center"
																				>
																					<ExternalLink className="h-3 w-3" />
																				</a>
																			)}
																		</>
																	) : (
																		<span className="text-gray-400 italic">
																			Not
																			in
																			top
																			30
																		</span>
																	)}
																</div>
															</div>
														</div>
													)}

													{/* Competitor rankings */}
													{competitorRankingsEngine.map(
														(comp) => (
															<div
																key={comp.id}
																className="space-y-1"
															>
																<div className="text-xs font-medium text-gray-700">
																	{
																		comp.domain
																	}
																</div>
																<div className="flex flex-wrap gap-2">
																	<div className="flex items-center space-x-1 text-xs">
																		<Image
																			src={
																				engineLogoSrc
																			}
																			alt={
																				engineAlt
																			}
																			width={
																				14
																			}
																			height={
																				14
																			}
																			className="flex-shrink-0"
																		/>
																		<span className="inline-flex items-center px-1.5 py-0.5 rounded font-medium bg-orange-100 text-orange-800">
																			#
																			{
																				comp.rankPosition
																			}
																		</span>
																		{comp.url && (
																			<a
																				href={
																					comp.url
																				}
																				target="_blank"
																				rel="noopener noreferrer"
																				className="text-blue-600 hover:text-blue-700 flex items-center"
																			>
																				<ExternalLink className="h-3 w-3" />
																			</a>
																		)}
																	</div>
																</div>
															</div>
														),
													)}

													{/* Gap opportunity indicator */}
													{isGap && (
														<div className="mt-2 p-2 bg-orange-100 border border-orange-200 rounded text-xs text-orange-900">
															<strong>
																Gap Opportunity:
															</strong>{" "}
															Competitors rank but
															you don't. Creating
															content for this
															query could improve
															your AI visibility.
														</div>
													)}
												</div>
											)}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}

/**
 * Standalone prominent grounding searches component for use outside of platform tabs
 */
export function GroundingSearchesHighlight({
	groundingSearches,
	platformName,
	brandDomain,
}: {
	groundingSearches: GroundingSearch[];
	platformName: string;
	brandDomain?: string;
}) {
	// Filter to only show gap opportunities
	const gapSearches = groundingSearches.filter((gs) => {
		if (!gs.rankings || gs.rankings.length === 0) return false;
		const brandRanked = gs.rankings.some(
			(r) => r.isOurBrand && r.rankPosition !== null,
		);
		const competitorRanked = gs.rankings.some(
			(r) => r.isCompetitor && r.rankPosition !== null,
		);
		return !brandRanked && competitorRanked;
	});

	if (gapSearches.length === 0) return null;

	return (
		<div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-4">
			<div className="flex items-center gap-2 mb-3">
				<div className="p-1.5 bg-amber-200 rounded-lg">
					<AlertTriangle className="h-4 w-4 text-amber-700" />
				</div>
				<h4 className="font-semibold text-gray-900">
					{gapSearches.length} Search Gap
					{gapSearches.length !== 1 ? "s" : ""} Detected
				</h4>
			</div>
			<p className="text-sm text-gray-700 mb-3">
				Competitors rank for these searches but you don't. Targeting
				these keywords can improve your AI visibility.
			</p>
			<div className="space-y-2">
				{gapSearches.slice(0, 3).map((search) => (
					<div
						key={search.id}
						className="bg-white rounded-lg p-3 border border-amber-200"
					>
						<p className="text-sm font-medium text-gray-900">
							"{search.query}"
						</p>
						<div className="flex flex-wrap gap-1 mt-2">
							{search.rankings
								.filter(
									(r) =>
										r.isCompetitor &&
										r.rankPosition !== null,
								)
								.slice(0, 3)
								.map((r) => (
									<span
										key={r.id}
										className="text-xs px-2 py-0.5 bg-orange-100 text-orange-800 rounded"
									>
										{r.domain} #{r.rankPosition}
									</span>
								))}
						</div>
					</div>
				))}
				{gapSearches.length > 3 && (
					<p className="text-xs text-gray-500 text-center">
						+{gapSearches.length - 3} more gap opportunities
					</p>
				)}
			</div>
		</div>
	);
}
